import React, { FC, useEffect, useState } from 'react'
import { Box, styled } from '@mui/material'
import { PillTabs } from 'components/PillTabs'
import { Tab } from 'components/PillTabs/Tab'
import { TabLabel } from 'components/PillTabs/TabLabel'
import { useTranslation } from 'react-i18next'

import { Search } from 'components'
import { mobileScreenWidth, smallScreenWidth } from 'utils'
import { useRisksFunctionalAccess } from 'hooks'
import { FUNCTIONAL_ACCESS_PAGE_SIZE } from 'utils'
import { Pagination } from 'packages/eid-ui'
import FunctionalAccessTable from 'components/FunctionalAccessRequest/FunctionalAccessTable'
import FunctionalAccessItemList from './FunctionalAccessList/FunctionalAccessList'

const TabsContainerBox = styled(Box)({
    backgroundColor: '#fbfbfd',
    borderBottom: '0.1rem solid #d8dadd',
    justifyContent: 'space-between',
    overflow: 'auto',

    '& .Tabs-root': {
        display: 'flex',
    },

    [`@media (max-width:${smallScreenWidth})`]: {
        flexDirection: 'column-reverse',
        paddingTop: '2rem',
        paddingLeft: '2rem',
        paddingRight: '2rem',

        '& .Tabs-root': {
            marginLeft: '-2rem',
        },
    },
})

const StyledTabsContainer = styled(Box)({
    display: 'flex',
    [`@media (max-width:${mobileScreenWidth}) `]: {
        display: 'block',
    },
})

const StyledTabsSearchContainer = styled(Box)({
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    backgroundColor: '#FAFAFC',
    borderBottom: 'solid 0.1rem #d8d8dd',
    alignItems: 'flex-end',
    paddingBottom: '0.8rem',
    '& > *': {
        marginRight: '2.6rem',
        width: '30.9rem',
        display: 'flex',
        alignItems: 'center',
        [`@media (max-width:${mobileScreenWidth}) `]: {
            width: '100%',
            marginLeft: '2.6rem',
            marginTop: '0.8rem',
        },
    },
})

const PaginationSteppersContainer = styled(Box)({
    width: '100%',
    padding: '1.6rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
})
export interface FunctionalAccessProps {
    assigneeId: any
    subjectId: any
}

const FunctionalAccess: FC<FunctionalAccessProps> = ({
    assigneeId,
    subjectId,
}) => {
    const { t } = useTranslation()
    let minimumRowsPerPage
    const [activeTab, setActiveTab] = useState('subjects')
    const [perPage, setPerPage] = useState(FUNCTIONAL_ACCESS_PAGE_SIZE)
    const [totalNumberOfPages, setTotalNumberOfPages] = useState<number>(0)

    const [searchTerm, setSearchTerm] = useState('')
    const [page, setPage] = useState(1)

    const handlePageChange = (value: any) => {
        setPage(value)
    }

    if (FUNCTIONAL_ACCESS_PAGE_SIZE < 10) {
        minimumRowsPerPage = FUNCTIONAL_ACCESS_PAGE_SIZE
    }

    // Using isfecthging instead of isLoading as isLoaidng value in not getting changes after first time load
    const { data: assigneeFunctionalAccess, isFetching } =
        useRisksFunctionalAccess(
            assigneeId,
            (page - 1) * perPage,
            perPage,
            searchTerm,
        )

    const { data: subjectFunctionalAccess, isFetching: isFetchingFunctional } =
        useRisksFunctionalAccess(
            subjectId,
            (page - 1) * perPage,
            perPage,
            searchTerm,
        )

    useEffect(() => {
        if (
            assigneeFunctionalAccess?.totalCount ||
            subjectFunctionalAccess?.totalCount
        ) {
            setTotalNumberOfPages(
                Math.ceil(
                    (activeTab === 'subjects'
                        ? subjectFunctionalAccess?.totalCount
                        : assigneeFunctionalAccess?.totalCount) / perPage,
                ),
            )
        }
    }, [
        assigneeFunctionalAccess?.totalCount,
        subjectFunctionalAccess?.totalCount,
        perPage,
        activeTab,
    ])

    return (
        <TabsContainerBox>
            <Box style={{marginTop:'0.1rem'}}>
                <StyledTabsContainer>
                    <PillTabs
                        value={activeTab}
                        onChange={(_, newValue: string) => {
                            setPerPage(FUNCTIONAL_ACCESS_PAGE_SIZE)
                            setTotalNumberOfPages(0)
                            setPage(1)
                            setSearchTerm('')
                            setActiveTab(newValue)
                        }}
                    >
                        <Tab
                            label={
                                <TabLabel
                                    label={t('SubjectsFunctionalAccess')}
                                    count={subjectFunctionalAccess?.totalCount}
                                />
                            }
                            value={'subjects'}
                        />
                        <Tab
                            label={
                                <TabLabel
                                    label={t('ItemFunctionalAccess')}
                                    count={assigneeFunctionalAccess?.totalCount}
                                />
                            }
                            value={'functional'}
                        />
                    </PillTabs>
                    <StyledTabsSearchContainer>
                        <Search
                            customStyle = {{  
                                width:"30.9rem !important" 
                            }}
                            outlined
                            placeholder={t('Search')}
                            value={searchTerm}
                            handleClear={() => setSearchTerm('')}
                            handleSearch={(value: any) => {
                                setSearchTerm(value)
                            }}
                        />
                    </StyledTabsSearchContainer>
                </StyledTabsContainer>
                <Box>
                    {activeTab === 'subjects' && (
                        <FunctionalAccessTable
                            latestData={subjectFunctionalAccess}
                            isFetching={isFetching}
                        />
                    )}
                    {activeTab === 'functional' && (
                        <FunctionalAccessItemList
                            latestData={assigneeFunctionalAccess}
                            isFetching={isFetchingFunctional}
                        />
                    )}
                    {!isFetching && (
                        <PaginationSteppersContainer>
                            <Pagination
                                count={totalNumberOfPages}
                                size="large"
                                page={page}
                                onChange={(_: any, value: any) =>
                                    handlePageChange(value)
                                }
                                parentIndex={199}
                                showPageSelection={true}
                                onPageSelection={(val: any) => setPage(val)}
                                minimumRowsPerPage={minimumRowsPerPage}
                                showItemsPerPageSelection={true}
                                onItemsPerPageChange={(val: any) => {
                                    setPage(1)
                                    setPerPage(val)
                                }}
                                itemsPerPage={perPage}
                                showCountStat={true}
                                totalCount={
                                    activeTab === 'subjects'
                                        ? subjectFunctionalAccess?.totalCount
                                        : assigneeFunctionalAccess?.totalCount
                                }
                            />
                        </PaginationSteppersContainer>
                    )}
                </Box>
            </Box>
        </TabsContainerBox>
    )
}

export default FunctionalAccess
